import cn from 'classnames';
import { useInView } from 'react-intersection-observer';
import { IProgram } from '../interface/IProgram';
import Program from './Program';
import styles from './channel-row.module.sass';

const componentClassName = 'program-container';

/**
 * Determines whether the provided element (or the nearest ancestor whose className includes "stack")
 * is marked as being dragged.
 *
 * @param target - The starting HTMLElement to inspect.
 * @returns A tuple where:
 *   - [0]: boolean indicating whether the found "stack" element is considered dragging.
 *   - [1]: the HTMLElement that was inspected (the matching "stack" element, or the original target if none found).
 */
const getDraggingStatus = (target: HTMLElement): [boolean, HTMLElement] => {
    if (target.className.indexOf(componentClassName) !== -1) {
        if (target.dataset.dragging) {
            return [target.dataset.dragging === 'true', target];
        }
        return [false, target];
    }
    if (target.parentElement) {
        return getDraggingStatus(target.parentElement as HTMLElement);
    }
    return [false, target];
};

// Memoized options to prevent recreation on every render
const USE_IN_VIEW_OPTIONS = {
    rootMargin: '600px 0px',
};

/**
 * This component manages program visibility through intersection observer and handles program selection or drag
 * interactions.
 * It renders the Program component only when it is in view, optimizing performance
 * Tracks drag state on elements to differentiate between clicks and drag interactions
 *
 * @param props.program - The program data object containing timing and channel information
 * @param props.onProgramSelect - Callback function invoked when a program is selected (non-drag click)
 * @param props.programRefs - Mutable ref object storing references to all program container elements
 * @param props.selectedProgram - The currently selected program, used to determine if the clicked program is already
 * selected
 */
const ProgramContainer = (props: IProgram) => {
    const { onProgramSelect, program, programRefs, selectedProgram } = props;

    const root = document.querySelector('.tvguide_epgs');
    const { ref, inView } = useInView({ ...USE_IN_VIEW_OPTIONS, root });

    return (
        <div
            ref={(el) => {
                if (el) {
                    programRefs.current.set(`${program?.since}|${program?.till}`, el);
                    ref(el);
                }
            }}
            onClick={(e) => {
                const [curDown, target] = getDraggingStatus(e.target as HTMLElement);

                if (curDown) {
                    // not needed?
                    target.dataset.dragging = 'false';

                    programRefs.current.forEach((ref: HTMLElement) => {
                        ref.dataset.dragging = 'false';
                    });
                } else {
                    if (program?.id !== selectedProgram?.id) {
                        onProgramSelect(program, program.channel);
                    }
                }
            }}
            data-since={program?.since} // used for identifying the program in the refs map
            className={cn(componentClassName, { [styles['program-container']]: !inView })}
        >
            {inView ? <Program {...props} /> : null}
        </div>
    );
};

export default ProgramContainer;
